import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CQModificationsFormFields } from "pages/CustomQuote/AddQuotePage/components/CQModifications/forms/CQModificationsFormSchema.ts";
import {
	calculateCustomizationChargeAmount,
	calculateManualPercentAdjustmentAmount,
	calculateQuoteTotal,
	sanitizeFlatAdjustment
} from "pages/CustomQuote/AddQuotePage/upchargeUtils.ts";
import useCqComplexityScores from "pages/CustomQuote/AddQuotePage/hooks/useCqComplexityScores.ts";
import { models } from "types/api/viewModels.ts";

interface UseChargesCalculationProps {
	baseQuoteTotal: number;
	complexityTierConfig: models["CustomQuoteConfigViewModel"] | undefined;
}

export const useChargesCalculation = ({ baseQuoteTotal, complexityTierConfig }: UseChargesCalculationProps) => {
	const { control } = useFormContext<CQModificationsFormFields>();

	const manualPercentAdjustment = useWatch({ control, name: "manualPercentAdjustment" });
	const manualFlatAdjustment = useWatch({ control, name: "manualFlatAdjustment" });
	const watchedCqMods = useWatch({ control, name: "cqMods" });
	const specialMods = useWatch({ control, name: "specialMods" });

	const normalizedCqMods = useMemo(
		() =>
			watchedCqMods?.map((mod) => ({
				id: mod?.id,
				sku: mod?.sku,
				description: mod?.description,
				value: mod?.value ?? undefined
			})) ?? [],
		[watchedCqMods]
	);

	const cqComplexityScoreTotal = useCqComplexityScores({ cqMods: normalizedCqMods ?? [] });

	const manualPercentAdjustmentAmount = useMemo(
		() => calculateManualPercentAdjustmentAmount(baseQuoteTotal, manualPercentAdjustment),
		[baseQuoteTotal, manualPercentAdjustment]
	);

	const manualFlatAdjustmentAmount = useMemo(
		() => sanitizeFlatAdjustment(manualFlatAdjustment),
		[manualFlatAdjustment]
	);

	const customizationChargeAmount = useMemo(
		() =>
			calculateCustomizationChargeAmount(
				baseQuoteTotal,
				specialMods ?? null,
				cqComplexityScoreTotal,
				complexityTierConfig
			),
		[baseQuoteTotal, specialMods, cqComplexityScoreTotal, complexityTierConfig]
	);

	const total = useMemo(
		() =>
			calculateQuoteTotal(
				baseQuoteTotal,
				specialMods ?? null,
				manualPercentAdjustment,
				manualFlatAdjustment,
				cqComplexityScoreTotal,
				complexityTierConfig
			),
		[
			baseQuoteTotal,
			specialMods,
			manualPercentAdjustment,
			manualFlatAdjustment,
			cqComplexityScoreTotal,
			complexityTierConfig
		]
	);

	return {
		manualPercentAdjustmentAmount,
		manualFlatAdjustmentAmount,
		customizationChargeAmount,
		total
	};
};
